import {
  Col,
  type ColumnType,
  type ColumnsType,
  Row,
  Table,
  type TableRef,
  Text,
} from '@/components';
import type { RequestProjectsParams } from '@/features/project/_hooks/project';
import type { PoliciesBaseDataModel } from '@/features/project/_models/filter-base-data';
import { PoliciesTableColumnLabelModifier } from '@/features/project/_models/project-filter';
import { type FC, useRef, useState } from 'react';
import type { z } from 'zod';
import { usePaginationState } from '../../_components/NestedTablePaginationProvider';
import type { ProjectListElementModel } from '../_models/projects';
import { ProjectsTable } from './ProjectsTable';

type LevelKey = 'level1' | 'level2' | 'level3';

type Project = z.infer<typeof ProjectListElementModel>;

type Props = {
  data: z.infer<typeof PoliciesBaseDataModel>;
  projectTableColumns: string[];
  rootPolicyLevel?: number;
  projectTableActionsColumn: ColumnType<Project>;
  pageSize?: number;
  requestParams: RequestProjectsParams;
  setSorting: (sorting: string) => void;
};

export const ExpandablePolicies: FC<Props> = ({
  data,
  projectTableColumns,
  rootPolicyLevel,
  projectTableActionsColumn,
  pageSize,
  requestParams,
  setSorting,
}) => {
  const { setPageNumber } = usePaginationState();
  const levelKey: LevelKey = (() => {
    const currentPolicyLevel = rootPolicyLevel ?? data[0]?.level;
    if (currentPolicyLevel === 3) {
      return 'level3';
    }
    if (currentPolicyLevel === 2) {
      return 'level2';
    }
    return 'level1';
  })();
  const policyColumnMap: Record<LevelKey, ColumnsType<(typeof data)[0]>> = {
    level1: [
      {
        title: PoliciesTableColumnLabelModifier.policyLevel1Name,
        dataIndex: 'name',
        key: 'policyLevel1Name',
      },
    ],
    level2: [
      {
        title: PoliciesTableColumnLabelModifier.policyLevel2Name,
        dataIndex: 'name',
        key: 'policyLevel2Name',
        width: 500,
        render: (value) => <Text weight="medium" text={value} maxWidth={500} />,
      },
    ],
    level3: [
      {
        title: PoliciesTableColumnLabelModifier.policyLevel3Name,
        dataIndex: 'name',
        key: 'policyLevel3Name',
        width: 500,
        render: (value) => <Text weight="medium" text={value} maxWidth={500} />,
      },
    ],
  };
  const tableRef = useRef<TableRef>(null);

  const [currentPolicyLevelPageNumber, setCurrentPolicyLevelPageNumber] =
    useState('1');

  const ScrollTo = (key: string) => () => {
    tableRef.current?.scrollTo({ key });
  };

  return (
    <Table
      enableSticky={false}
      ref={tableRef}
      columns={policyColumnMap[levelKey]}
      dataSource={data}
      rowKey="id"
      pagination={{
        current: Number(currentPolicyLevelPageNumber),
        pageSize: Number(pageSize),
        total: data.length,
        showSizeChanger: false,
        onChange: (page) => {
          setCurrentPolicyLevelPageNumber(page.toString());
        },
      }}
      tableLayout="fixed"
      expandable={{
        expandedRowRender: (policies) => (
          <>
            {policies.descendant.length ? (
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <ExpandablePolicies
                    data={policies.descendant}
                    pageSize={pageSize}
                    projectTableColumns={projectTableColumns}
                    projectTableActionsColumn={projectTableActionsColumn}
                    requestParams={requestParams}
                    setSorting={setSorting}
                  />
                </Col>
              </Row>
            ) : (
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <ProjectsTable
                    policyId={policies.id}
                    pageSize={pageSize}
                    columnsSettings={projectTableColumns}
                    tableActionsColumn={projectTableActionsColumn}
                    scrollToTop={ScrollTo(policies.id)}
                    requestParams={requestParams}
                    setSorting={setSorting}
                  />
                </Col>
              </Row>
            )}
          </>
        ),
        onExpand: (_, record) => {
          if (!record.descendant.length) {
            setPageNumber(1);
          }
        },
      }}
    />
  );
};
